import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as HorsesShareActions from '../actions/horses-share.actions';
import { catchError, map, mergeMap, switchMap } from 'rxjs';
import { HorseShareService } from '../services/horse-share.service';
import { HorseShareActionResponseTypeEnum } from '@sleipai/sleip-models';

@Injectable()
export class HorsesShareEffects {
  loadHorseShareByGroupIdAndHorseId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesShareActions.loadHorseShareInfo),
      mergeMap(({ groupId, horseId, userId }) => {
        const singleHorseShareItem = this.horseShareService.getHorseShareInfo(
          groupId,
          horseId,
          userId
        );
        return singleHorseShareItem.pipe(
          map((data) => {
            return HorsesShareActions.loadHorseShareInfoSuccess({ data });
          })
        );
      })
    );
  });

  createHorseShare$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesShareActions.saveHorseShareInfo),
      map((action) => action),
      switchMap(({ data }) => {
        return this.horseShareService.createHorseShare(data).pipe(
          switchMap(() => {
            return [
              HorsesShareActions.saveHorseShareInfoSuccess({
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          }),
          catchError((error) => {
            const errorResponse = {
              ...error,
              message:
                error?.message ??
                'An error occurred, New Horse Share Creation Failed',
            };
            return [
              HorsesShareActions.saveHorseShareInfoFailure({
                error: errorResponse,
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARE_ACTION_FAILED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          })
        );
      })
    );
  });

  
  adminCreateHorseShare$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesShareActions.adminSaveHorseShareInfo),
      switchMap(({ data }) => {
        return this.horseShareService.adminCreateHorseShare(data).pipe(
          switchMap(() => {
            return [
              HorsesShareActions.saveHorseShareInfoSuccess({
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          }),
          catchError((error) => {
            const errorResponse = {
              ...error,
              message:
                error?.message ??
                'An error occurred, New Horse Share Creation Failed',
            };
            return [
              HorsesShareActions.saveHorseShareInfoFailure({
                error: errorResponse,
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARE_ACTION_FAILED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          })
        );
      })
    );
  });

  stopHorseShare$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesShareActions.stopHorseShare),
      map((action) => action),
      switchMap(({ data }) => {
        return this.horseShareService.stopHorseShare(data).pipe(
          switchMap(() => {
            return [
              HorsesShareActions.saveHorseShareInfoSuccess({
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARE_DEACTIVATED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          }),
          catchError((error) => {
            const errorResponse = {
              ...error,
              message: 'An error occurred, Stopping Horse Share Failed',
            };
            return [
              HorsesShareActions.saveHorseShareInfoFailure({
                error: errorResponse,
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARE_ACTION_FAILED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          })
        );
      })
    );
  });

  reactivateHorseShare$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesShareActions.reactivateHorseShare),
      map((action) => action),
      switchMap(({ data }) => {
        return this.horseShareService.reactivateHorseShare(data).pipe(
          switchMap(() => {
            return [
              HorsesShareActions.saveHorseShareInfoSuccess({
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARE_REACTIVATED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          }),
          catchError((error) => {
            const errorResponse = {
              ...error,
              message: 'An error occurred, Reactivating Horse Share Failed',
            };
            return [
              HorsesShareActions.saveHorseShareInfoFailure({
                error: errorResponse,
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARE_ACTION_FAILED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          })
        );
      })
    );
  });

  batchShareHorses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(HorsesShareActions.batchShareHorses),
      switchMap(({ data }) => {
        return this.horseShareService.batchShareHorses(data).pipe(
          switchMap(() => {
            return [
              HorsesShareActions.saveHorseShareInfoSuccess({
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSES_BATCH_SHARED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          }),
          catchError((error) => {
            const errorResponse = {
              ...error,
              message: error?.message ?? 'An error occurred, Batch Horse Share Failed',
            };
            return [
              HorsesShareActions.saveHorseShareInfoFailure({
                error: errorResponse,
                horseShareActionResponseType:
                  HorseShareActionResponseTypeEnum.HORSE_SHARE_ACTION_FAILED,
              }),
              HorsesShareActions.resetHorseSharedSavedFlag(),
            ];
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private horseShareService: HorseShareService
  ) {}
}
